import React from "react";
import SocialLoginButton from "../buttons/SocialLoginButton";
import TextField from "../inputs/TextField";
import Divider from "../visuals/Divider";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link } from "../buttons";
import {
  Stack,
  styled,
  Typography,
  Button,
  ButtonProps,
  InputAdornment,
  IconButton,
} from "@mui/material";

export interface LoginFormInterface {
  enterpriseLogin?: boolean;
  handleAction: (type: any, data?: any) => void;
  initialData: {
    email: string;
    password: string;
  };
  schema: any;
}

const StyledButton = styled(Button)<ButtonProps>(({ theme }: any) => ({
  backgroundColor: theme.palette.common["black"],
  "&:hover , &:active, &:focus": {
    backgroundColor: theme.palette.common["black"],
  },
}));

const LoginForm = ({
  handleAction,
  enterpriseLogin,
  initialData,
  schema,
}: LoginFormInterface) => {
  const { values, handleChange, handleBlur, handleSubmit, touched, errors } =
    useFormik({
      initialValues: initialData,
      validationSchema: Yup.object().shape(schema),
      onSubmit: (value) => handleAction("submit", value),
    });
  const [showPassword, setShowPassword] = React.useState(false);
  return (
    <form onSubmit={handleSubmit} style={{ width: "100%", height: "100%" }}>
      <Stack
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        width="100%"
        height="100%"
      >
        <Stack padding={2} spacing={1.5} textAlign="center">
          <Typography variant="h1" fontWeight={700}>
            Welcome Back!
          </Typography>
          <Typography variant="h4" fontWeight={400}>
            Login to your account
          </Typography>
        </Stack>

        <Divider text="Or continue with" />
        <Stack direction="row" spacing={1.5}>
          <SocialLoginButton
            type="google"
            label="Google"
            onClick={() => handleAction("google-login")}
          />
          <SocialLoginButton
            type="facebook"
            label="Facebook"
            onClick={() => handleAction("facebook-login")}
          />
          <SocialLoginButton
            type="linkedIn"
            label="LinkedIn"
            onClick={() => handleAction("linkedin-login")}
          />
        </Stack>

        <TextField
          label="Email"
          name="email"
          id="email"
          placeholder="Email"
          helperText={touched.email ? errors.email : ""}
          error={touched.email && Boolean(errors.email)}
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <TextField
          label="Password"
          name="password"
          id="password"
          placeholder="*********"
          helperText={touched.password ? errors.password : ""}
          error={touched.password && Boolean(errors.password)}
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? (
                    <i className="far fa-eye font-black"></i>
                  ) : (
                    <i className="far fa-eye-slash font-black"></i>
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => handleSubmit()}
        >
          Login
        </Button>
        {enterpriseLogin ? (
          <Stack spacing={3} width="100%">
            <Divider text="Or" />
            <StyledButton
              variant="contained"
              fullWidth
              onClick={() => handleAction("enterprizeLogin")}
            >
              Enterprize Login
            </StyledButton>
          </Stack>
        ) : null}
        <Stack
          width="100%"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontSize={(theme: any) => theme.typography.h5.fontSize}>
            Don’t have an account yet?
            <Link
              ml="5px"
              onClick={() => handleAction("signup")}
              label={"Sign up"}
            />
          </Typography>
          <Link
            color="green"
            onClick={() => handleAction("forgotPassword")}
            label="Forgot your password?"
          />
        </Stack>
      </Stack>
    </form>
  );
};
export default LoginForm;
